import { useState } from 'react';
import { X, Mail, Lock, User, Eye, EyeOff, Loader2, Users, Target } from 'lucide-react';
import { useAuth, UserType } from '../contexts/AuthContext';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [resendingEmail, setResendingEmail] = useState(false);
  const [userType, setUserType] = useState<UserType>('sdr');
  const [showConfirmEmail, setShowConfirmEmail] = useState(false);

  const { signIn, signUp } = useAuth();

  const handleResendConfirmation = async () => {
    if (!email) {
      setError('Digite seu email primeiro');
      return;
    }

    try {
      setResendingEmail(true);
      setError('');
      
      // Tentar fazer login para verificar se o email existe
      const { error } = await signIn(email, 'dummy_password');
      
      if (error?.message?.includes('Email not confirmed')) {
        // Se o email não foi confirmado, reenviar confirmação
        const { error: resendError } = await signUp(email, 'dummy_password');
        if (resendError) {
          setError('Erro ao reenviar email. Tente criar uma nova conta.');
        } else {
          setSuccess('Email de confirmação reenviado! Verifique sua caixa de entrada.');
        }
      } else {
        setError('Email não encontrado ou já confirmado.');
      }
    } catch (err) {
      setError('Erro ao reenviar email. Tente novamente.');
    } finally {
      setResendingEmail(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      let result;
      if (isLogin) {
        result = await signIn(email, password);
      } else {
        result = await signUp(email, password, userType);
        if (!result.error) {
          setShowConfirmEmail(true);
          setIsLogin(true);
        }
      }

      if (result.error) {
        console.error('Erro detalhado:', result.error);
        
        // Mensagens de erro mais específicas
        let errorMessage = result.error.message || 'Erro ao fazer login/cadastro';
        
        if (result.error.message?.includes('Invalid login credentials')) {
          errorMessage = 'Email ou senha incorretos. Verifique suas credenciais.';
        } else if (result.error.message?.includes('Email not confirmed')) {
          errorMessage = 'Email não confirmado. Verifique sua caixa de entrada e clique no link de confirmação.';
        } else if (result.error.message?.includes('Too many requests')) {
          errorMessage = 'Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.';
        } else if (result.error.message?.includes('Password should be at least')) {
          errorMessage = 'A senha deve ter pelo menos 6 caracteres.';
        } else if (result.error.message?.includes('User not found')) {
          errorMessage = 'Usuário não encontrado. Verifique o email ou crie uma conta.';
        }
        
        setError(errorMessage);
      } else if (isLogin) {
        onClose();
      }
    } catch (err) {
      setError('Erro inesperado. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setError('');
    setSuccess('');
    setShowPassword(false);
    setUserType('sdr');
    setShowConfirmEmail(false);
  };

  const handleModeChange = (newIsLogin: boolean) => {
    setIsLogin(newIsLogin);
    resetForm();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {isLogin ? 'Entrar no CRM' : 'Criar Conta'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="seu@email.com"
                required
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Senha
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Sua senha"
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                )}
              </button>
            </div>
          </div>

          {/* User Type Toggle - Only show on signup */}
          {!isLogin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Tipo de Usuário
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setUserType('sdr')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    userType === 'sdr'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <Users className="h-6 w-6" />
                    <span className="font-medium">SDR</span>
                    <span className="text-xs text-center">
                      Sales Development Representative
                    </span>
                  </div>
                </button>
                
                <button
                  type="button"
                  onClick={() => setUserType('closer')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    userType === 'closer'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <Target className="h-6 w-6" />
                    <span className="font-medium">Closer</span>
                    <span className="text-xs text-center">
                      Especialista em Fechamento
                    </span>
                  </div>
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                💡 Escolha seu perfil para personalizar sua experiência no CRM
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-700">{error}</p>
              {error.includes('Email não confirmado') && (
                <button
                  type="button"
                  onClick={handleResendConfirmation}
                  disabled={resendingEmail}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline disabled:opacity-50"
                >
                  {resendingEmail ? 'Reenviando...' : 'Reenviar email de confirmação'}
                </button>
              )}
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-md p-3">
              <p className="text-sm text-green-700">{success}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-2 px-4 rounded-md transition-colors flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {isLogin ? 'Entrar' : 'Criar Conta'}
          </button>

          {/* Mode Toggle */}
          <div className="text-center pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              {isLogin ? 'Não tem uma conta?' : 'Já tem uma conta?'}
              <button
                type="button"
                onClick={() => handleModeChange(!isLogin)}
                className="ml-1 text-blue-600 hover:text-blue-800 font-medium"
              >
                {isLogin ? 'Criar conta' : 'Fazer login'}
              </button>
            </p>
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <p className="text-xs text-blue-700">
              💡 <strong>Dica:</strong> Para criar uma conta, você receberá um email de confirmação.
            </p>
          </div>
        </form>
      </div>

      {/* Confirm Email Popup */}
      {showConfirmEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Confirme seu email</h3>
              <p className="text-gray-600 mb-4">
                Enviamos um link de confirmação para <strong>{email}</strong>
              </p>
              <button
                onClick={() => {
                  setShowConfirmEmail(false);
                  onClose();
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginModal;
